import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, pillClass, statusLabel, timeAgo, fmtDate, isOverdue, waShareUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AlertTriangle, Calendar, Megaphone, ClipboardList, ArrowRight, Plus } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [issues, setIssues] = useState([]);
  const [actions, setActions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [notices, setNotices] = useState([]);
  const [istats, setIstats] = useState({});
  const [astats, setAstats] = useState({});
  const [community, setCommunity] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  const loadAll = useCallback(() => {
    Promise.all([
      api.get("/issues").then((r) => setIssues(r.data)),
      api.get("/actions").then((r) => setActions(r.data)),
      api.get("/meetings").then((r) => setMeetings(r.data)),
      api.get("/notices").then((r) => setNotices(r.data)),
      api.get("/issues/stats/overview").then((r) => setIstats(r.data)),
      api.get("/actions/stats/overview").then((r) => setAstats(r.data)),
      api.get("/communities/mine").then((r) => setCommunity(r.data)),
    ]).catch((e) => console.error("dashboard load failed", e));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  async function generateDigest(period) {
    setDigestBusy(true);
    try {
      const { data } = await api.get("/digest", { params: { period } });
      window.open(waShareUrl(data.text), "_blank");
      toast.success("Digest opened in WhatsApp");
    } catch { toast.error("Could not generate digest"); }
    finally { setDigestBusy(false); }
  }

  const isCommittee = user?.role === "committee";
  const myFlatIssues = issues.filter((i) => i.flat_id === user?.flat_id);
  const commonIssues = issues.filter((i) => i.is_common_area);
  const myIssues = issues.filter((i) => i.assigned_to === user?.id || i.created_by === user?.id);
  const myTodos = actions.filter((a) => a.owner_user_id === user?.id && a.status !== "done");

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-6xl">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{isCommittee ? "Committee" : "Resident"} dashboard</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Hi {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="text-stone-600 text-sm mt-1" data-testid="dash-community">{community?.name ? `${community.name} · ` : ""}Here's what's happening at your building today.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/issues/new" data-testid="dash-cta-issue"
            className="hidden md:inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C05640] hover:bg-[#A54734] text-white font-medium">
            <Plus size={18} /> Raise an issue
          </Link>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
        <StatCard icon={AlertTriangle} label="Open issues"
          value={(istats.open ?? 0) + (istats.in_progress ?? 0)} accent
          onClick={() => nav("/issues?status=new")} testid="tile-open" />
        <StatCard icon={ClipboardList} label="Overdue to-dos"
          value={astats.overdue ?? 0} alert={(astats.overdue ?? 0) > 0}
          onClick={() => nav("/todos")} testid="tile-overdue" />
        <StatCard icon={Calendar} label="Meetings" value={meetings.length}
          onClick={() => nav("/meetings")} testid="tile-meetings" />
        <StatCard icon={Megaphone} label="Notices" value={notices.length}
          onClick={() => nav("/notices")} testid="tile-notices" />
      </section>

      {isCommittee && <DigestBar busy={digestBusy} onGenerate={generateDigest} />}

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        {!isCommittee && (myIssues.length > 0 || myTodos.length > 0) && (
          <AssignedToMe myIssues={myIssues} myTodos={myTodos} userId={user?.id} />
        )}

        <Section title={isCommittee ? "Recent issues" : "Common area issues"} link="/issues" testid="sec-issues" className="lg:col-span-2">
          {(isCommittee ? issues : commonIssues).slice(0, 5).map((i) => (
            <Link key={i.id} to={`/issues/${i.id}`} data-testid={`issue-row-${i.id}`}
                  className="block px-4 py-3.5 rounded-xl hover:bg-stone-50 border border-stone-200 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-stone-500">{i.number}</span>
                    <span className="font-medium text-stone-900 truncate">{i.title}</span>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {i.category} · raised by {i.creator_name} · {timeAgo(i.created_at)}
                  </div>
                </div>
                <span className={pillClass(i.status)}>{statusLabel(i.status)}</span>
              </div>
            </Link>
          ))}
          {issues.length === 0 && <Empty text="No issues yet." />}
        </Section>

        <Section title="To-dos" link="/todos" testid="sec-actions">
          {actions.slice(0, 6).map((a) => (
            <div key={a.id} className="px-4 py-3 rounded-xl border border-stone-200" data-testid={`action-row-${a.id}`}>
              <div className="text-sm font-medium text-stone-900">{a.title}</div>
              <div className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                <span className={pillClass(isOverdue(a.due_date, a.status) ? "overdue" : a.status)}>
                  {isOverdue(a.due_date, a.status) ? "Overdue" : statusLabel(a.status)}
                </span>
                <span>· due {fmtDate(a.due_date)}</span>
              </div>
            </div>
          ))}
          {actions.length === 0 && <Empty text="Nothing pending." />}
        </Section>

        <Section title="Latest notices" link="/notices" testid="sec-notices" className="lg:col-span-2">
          {notices.slice(0, 4).map((n) => (
            <Link key={n.id} to="/notices" data-testid={`notice-row-${n.id}`}
                  className={`block px-4 py-3 rounded-xl border ${n.pinned ? "border-[#C05640] bg-[#FDFBF7]" : "border-stone-200"}`}>
              <div className="flex items-center gap-2">
                {n.pinned && <span className="text-[10px] uppercase tracking-wider font-bold text-[#C05640]">Pinned</span>}
                <span className="text-sm font-medium text-stone-900">{n.title}</span>
              </div>
              <p className="text-xs text-stone-600 mt-1 line-clamp-2">{n.body}</p>
            </Link>
          ))}
          {notices.length === 0 && <Empty text="No notices yet." />}
        </Section>

        {(isCommittee || myFlatIssues.length > 0) && (
          <Section title={isCommittee ? "Upcoming meetings" : "My flat"} link={isCommittee ? "/meetings" : "/issues?mine=1"} testid="sec-side">
            {isCommittee
              ? meetings.slice(0, 4).map((m) => (
                  <Link key={m.id} to={`/meetings/${m.id}`} className="block px-4 py-3 rounded-xl border border-stone-200" data-testid={`meeting-row-${m.id}`}>
                    <div className="text-sm font-medium">{m.title}</div>
                    <div className="text-xs text-stone-500">{fmtDate(m.date)} · {m.type}{m.locked && " · 🔒 locked"}</div>
                  </Link>
                ))
              : myFlatIssues.slice(0, 4).map((i) => (
                  <Link key={i.id} to={`/issues/${i.id}`} className="block px-4 py-3 rounded-xl border border-stone-200">
                    <div className="text-sm font-medium">{i.title}</div>
                    <div className="text-xs text-stone-500"><span className={pillClass(i.status)}>{statusLabel(i.status)}</span></div>
                  </Link>
                ))}
            {((isCommittee ? meetings : myFlatIssues).length === 0) && <Empty text="Nothing here yet." />}
          </Section>
        )}
      </div>
    </div>
  );
}

const DIGEST_PERIODS = [
  ["week", "1 week", "covers last 7 days"],
  ["fortnight", "2 weeks", "covers last 14 days"],
  ["month", "1 month", "covers last 30 days"],
  ["quarter", "3 months", "covers last 90 days"],
];

function DigestBar({ busy, onGenerate }) {
  return (
    <div className="mt-5 bg-[#FDFBF7] border border-[#E8D8D3] rounded-2xl p-4 flex items-center gap-3 flex-wrap" data-testid="digest-actions">
      <FaWhatsapp size={18} className="text-[#25D366]" />
      <span className="text-sm font-medium text-stone-800">Share a digest on WhatsApp:</span>
      {DIGEST_PERIODS.map(([p, label, hint]) => (
        <button key={p} onClick={() => onGenerate(p)} disabled={busy}
          data-testid={`digest-${p}`}
          title={hint}
          className="px-3 py-1 rounded-full text-xs border border-stone-300 bg-white hover:border-[#C05640] hover:text-[#C05640]">{label}</button>
      ))}
      <span className="text-[11px] text-stone-500 w-full md:w-auto">Each digest summarises issues, meetings, notices and pending to-dos for that period — the date range is included in the message.</span>
    </div>
  );
}

function AssignedToMe({ myIssues, myTodos, userId }) {
  return (
    <Section title="Assigned to me" link="/todos" testid="sec-assigned-me" className="lg:col-span-3">
      <div className="grid sm:grid-cols-2 gap-2.5">
        {myIssues.slice(0, 4).map((i) => (
          <Link key={i.id} to={`/issues/${i.id}`} data-testid={`mine-issue-${i.id}`}
                className="block px-4 py-3 rounded-xl border border-stone-200 hover:border-stone-400">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-stone-500">#{i.number}</span>
              <span className="text-sm font-medium truncate">{i.title}</span>
            </div>
            <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
              <span className={pillClass(i.status)}>{statusLabel(i.status)}</span>
              <span>·</span>
              <span>{i.created_by === userId ? "Raised by me" : "Assigned to me"}</span>
            </div>
          </Link>
        ))}
        {myTodos.slice(0, 4).map((a) => (
          <Link key={a.id} to="/todos" data-testid={`mine-todo-${a.id}`}
                className="block px-4 py-3 rounded-xl border border-stone-200 hover:border-stone-400">
            <div className="text-sm font-medium truncate">{a.title}</div>
            <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
              <span className={pillClass(isOverdue(a.due_date, a.status) ? "overdue" : a.status)}>
                {isOverdue(a.due_date, a.status) ? "Overdue" : statusLabel(a.status)}
              </span>
              <span>· To-do due {fmtDate(a.due_date)}</span>
            </div>
          </Link>
        ))}
      </div>
      {myIssues.length === 0 && myTodos.length === 0 && <Empty text="Nothing on your plate. 🌿" />}
    </Section>
  );
}

function StatCard({ icon: Icon, label, value, accent, alert, onClick, testid }) {
  let iconCls = "bg-stone-100 text-stone-600";
  if (accent) iconCls = "bg-[#E8D8D3] text-[#C05640]";
  else if (alert) iconCls = "bg-red-100 text-red-700";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left bg-white border rounded-2xl p-4 transition hover:border-stone-400 hover:shadow-sm active:scale-[0.98] ${alert ? "border-red-200 bg-red-50" : "border-stone-200"}`}>
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${iconCls}`}><Icon size={18} /></div>
      <div className="mt-3 font-serif-display text-3xl font-semibold">{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
    </button>
  );
}

function Section({ title, link, testid, children, className = "" }) {
  return (
    <section className={`bg-white rounded-2xl border border-stone-200 p-5 ${className}`} data-testid={testid}>
      <header className="flex items-center justify-between mb-4">
        <h2 className="font-serif-display text-xl font-medium">{title}</h2>
        {link && <Link to={link} className="text-xs text-[#C05640] hover:underline inline-flex items-center gap-1">See all <ArrowRight size={12} /></Link>}
      </header>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function Empty({ text }) { return <div className="text-sm text-stone-500 italic px-1">{text}</div>; }
